/* What the spot wallet becomes in the book.
 *
 * Counted in, not shown beside: an account of its own, "MEXC spot", holding the
 * stablecoins as its cash and each coin as a holding priced the way every other
 * coin in the book is. Pure, so what a read would write can be tested and shown
 * before anything is.
 *
 * Three things it does not do:
 *
 *   - Invent a cost. MEXC reports what is held, not what it was bought for, so a
 *     coin new to the book comes in at an unknown cost (§ no invented data) and
 *     says so until one is entered.
 *   - Remove anything. A coin the book holds that the wallet no longer has is
 *     listed as gone, the way a contract closed at the exchange is, and left for
 *     you — selling it has a price the balance does not carry.
 *   - Count dust. Anything worth under a dollar, or with no USDT pair to price
 *     it, is left out and listed, so airdrop crumbs do not become holdings. */

import type { AccountRecord, Holding } from '../types';
import type { SpotBalance } from '../lib/mexcSpot';

export const SPOT_ACCOUNT = 'mexc-spot';

/** Held as cash, one to one with the dollar. */
const STABLE = new Set(['USDT', 'USDC', 'USD1', 'FDUSD', 'USDE', 'TUSD', 'DAI']);

export const DUST_USD = 1;

export interface SpotPlan {
  /** The account to create, when the book has none for the spot wallet yet. */
  newAccount: AccountRecord | null;
  cashUsd: number;
  /** The part of the cash tied up in open orders. */
  lockedCashUsd: number;
  /** Each coin's holding as it should stand. */
  holdings: Holding[];
  gone: string[];
  /** Pairs to ask for open orders. */
  pairs: string[];
  dust: string[];
}

export function spotPlan(
  balances: SpotBalance[],
  prices: Map<string, number>,
  book: { accounts: AccountRecord[]; holdings: Holding[] },
  now: string,
): SpotPlan {
  const newAccount: AccountRecord | null = book.accounts.some((a) => a.id === SPOT_ACCOUNT) ? null : {
    id: SPOT_ACCOUNT,
    name: 'MEXC spot',
    currency: 'USD',
    order: Math.max(-1, ...book.accounts.map((a) => a.order)) + 1,
  };

  let cashUsd = 0;
  let lockedCashUsd = 0;
  const holdings: Holding[] = [];
  const pairs: string[] = [];
  const dust: string[] = [];
  const kept = new Set<string>();

  for (const b of balances) {
    const asset = b.asset.toUpperCase();
    const quantity = b.free + b.locked;
    if (!(quantity > 0)) continue;

    if (STABLE.has(asset)) {
      cashUsd += quantity;
      lockedCashUsd += b.locked;
      continue;
    }

    const before = book.holdings.find(
      (h) => h.account === SPOT_ACCOUNT && h.ticker.toUpperCase() === asset,
    );
    const price = prices.get(`${asset}USDT`);
    // Already in the book, it is followed whatever it is worth; new, it has to be worth having.
    if (!before && (price === undefined || quantity * price < DUST_USD)) {
      dust.push(asset);
      continue;
    }

    kept.add(asset);
    pairs.push(`${asset}USDT`);
    holdings.push(before ? { ...before, shares: quantity } : {
      id: `${SPOT_ACCOUNT}-${asset.toLowerCase()}`,
      ticker: asset,
      yahooSymbol: `${asset}-USD`,
      account: SPOT_ACCOUNT,
      shares: quantity,
      avgCost: 0,
      currency: 'USD',
      openedAt: now.slice(0, 10),
      assetKind: 'crypto',
    });
  }

  const gone = book.holdings
    .filter((h) => h.account === SPOT_ACCOUNT && h.shares > 0 && !kept.has(h.ticker.toUpperCase()))
    .map((h) => h.ticker);

  return { newAccount, cashUsd, lockedCashUsd, holdings, gone, pairs, dust };
}
